import type { Directive } from "vue";

// A singleton ResizeObserver observing any elements registered via the directive
// Stores per-element animation state in a WeakMap to prevent leaks

type ElementState = {
    lastHeight: number | null;
    animating: boolean;
    duration: number;
    easing: string;
};

const stateMap = new WeakMap<HTMLElement, ElementState>();
let ro: ResizeObserver | null = null;

function ensureObserver() {
    if (!ro) {
        ro = new ResizeObserver(entries => {
            for (const entry of entries) {
                const el = entry.target as HTMLElement;
                const st = stateMap.get(el);
                if (!st) continue;

                const newHeight = entry.contentRect.height;

                if (!st.animating && st.lastHeight !== null && newHeight !== st.lastHeight) {
                    const prevOverflow = el.style.overflow;
                    const prevTransition = el.style.transition;

                    el.style.overflow = "hidden";
                    el.style.transition = `height ${st.duration}ms ${st.easing}`;
                    el.style.height = st.lastHeight + "px";

                    // force reflow
                    void el.offsetHeight;

                    st.animating = true;
                    el.style.height = newHeight + "px";

                    setTimeout(() => {
                        el.style.height = "auto";
                        el.style.overflow = prevOverflow;
                        el.style.transition = prevTransition;
                        st.animating = false;
                        st.lastHeight = el.getBoundingClientRect().height;
                    }, st.duration);
                } else if (!st.animating) {
                    st.lastHeight = newHeight;
                }
            }
        });
    }
    return ro!;
}

function parseBinding(binding: unknown): { duration: number; easing: string } {
    const def = { duration: 300, easing: "ease" };
    if (!binding || typeof binding !== "object") return def;
    const val = binding as Record<string, unknown>;
    const duration = typeof val.duration === "number" ? val.duration : def.duration;
    const easing = typeof val.easing === "string" ? val.easing : def.easing;
    return { duration, easing };
}

export const vAutoHeight: Directive<HTMLElement, { duration?: number; easing?: string }> = {
    mounted(el, { value }) {
        const { duration, easing } = parseBinding(value);
        stateMap.set(el, { lastHeight: null, animating: false, duration, easing });
        ensureObserver().observe(el);
    },
    updated(el, { value }) {
        // allow dynamic duration/easing updates
        const st = stateMap.get(el);
        if (st) {
            const { duration, easing } = parseBinding(value);
            st.duration = duration;
            st.easing = easing;
        }
    },
    unmounted(el) {
        ro?.unobserve(el);
        stateMap.delete(el);
    },
};
